import React, { useMemo, useRef } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { PamGrid, useGridConfigs } from '@1823-partners/core';
import { SetFilterModule } from 'ag-grid-enterprise';

import api from '../../api';
import {
  RECONCILIATION_LEGEND,
  emptyBuckets,
  excelStyles,
  money,
  reconciliationColumns,
  topContributors,
  totalsRow,
} from './remittanceColumns';

const getLoanRowId = (data) => String(data.CW_ID);

const CURRENCY = (value) => money({ value });

/**
 * One run's reconciliation — the artifact that replaces the manual workbook, so it carries every
 * loan in scope rather than only the breaks.
 *
 * The TOTAL row is pinned to the bottom rather than being a data row. The contributors strip sits
 * above it to keep the largest components of the net difference visible during review.
 */
const ReconciliationGrid = ({ loans, isLoading, period, height = 520 }) => {
  const gridRef = useRef(null);

  const hidden = useMemo(() => emptyBuckets(loans), [loans]);
  const columnDefs = useMemo(() => reconciliationColumns(hidden), [hidden]);
  const pinnedBottom = useMemo(() => totalsRow(loans), [loans]);
  const contributors = useMemo(() => topContributors(loans), [loans]);

  const totalDiffNet = pinnedBottom[0]?.DIFF_NET ?? 0;

  // exportDataAsExcel ignores pinned rows, and the manual Rec tab ends with a TOTAL — so append it
  // explicitly, in the same column order the grid is showing (which respects hidden buckets).
  const exportConfig = useMemo(() => {
    const total = pinnedBottom[0];
    const config = {
      fileName: `CMLReconciliation_${period || 'run'}.xlsx`,
      sheetName: 'Rec',
    };
    if (!total) return config;
    config.appendContent = [
      {
        cells: columnDefs.map((col) => {
          const value = total[col.field];
          const isMoney = typeof value === 'number';
          return {
            data: {
              value: col.field === 'CW_ID' ? 'TOTAL' : isMoney ? String(value) : '',
              type: isMoney && col.field !== 'CW_ID' ? 'Number' : 'String',
            },
            styleId: 'remitTotal',
          };
        }),
      },
    ];
    return config;
  }, [columnDefs, pinnedBottom, period]);

  const gridConfigs = useGridConfigs(api, {
    app: 'recon',
    gridId: 'remittance-reconciliation',
    gridApiRef: gridRef,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {contributors.length > 0 && (
        <Box sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              Diff Net {CURRENCY(totalDiffNet)} — largest contributors:
            </Typography>
            {contributors.map((row) => (
              <Chip
                key={row.CW_ID}
                size="small"
                label={`${row.LOAN_NAME || row.CW_ID} ${CURRENCY(row.DIFF_NET)}`}
                title={`${row.CW_ID} · ${row.COVERAGE}`}
                sx={{ height: 20, fontSize: 11 }}
              />
            ))}
          </Stack>
        </Box>
      )}

      <PamGrid
        ref={gridRef}
        rowData={loans}
        columnDefs={columnDefs}
        getRowId={getLoanRowId}
        height={`${height}px`}
        // A data row would sort like a loan and disappear under a filter; pinning keeps it put.
        pinnedBottomRowData={pinnedBottom}
        showToolbar
        title={period ? `Reconciliation — ${period}` : 'Reconciliation'}
        showExport
        exportConfig={exportConfig}
        // Header fills, the accounting number format and the TOTAL row's border, taken from the
        // manual Rec tab so the export reads like the workbook it replaces.
        excelStyles={excelStyles}
        showQuickFilter
        quickFilterPlaceholder="Filter loans..."
        // The row markers and Diff shading are not self-explanatory on their own.
        showLegend
        legendItems={RECONCILIATION_LEGEND}
        onGridReady={gridConfigs.onGridReady}
        onFilterChanged={gridConfigs.onGridStateChanged}
        rowClassRules={{
          'remit-row-open': (p) => p.data?.FLAGGED && !p.data?.ACCEPTED,
          'remit-row-signed-off': (p) => p.data?.ACCEPTED,
        }}
        isLoading={isLoading}
        loadingMessage="Loading reconciliation..."
        modules={[SetFilterModule]}
      />
    </Box>
  );
};

export default ReconciliationGrid;
